/**
 * One definition of the ripening program fields, shared by the program creator,
 * the program preview and the running program editor so every screen uses the same names.
 *
 * `key` is the field name the ripening room uses. Only the program creator (an admin
 * screen) shows it; ripener screens show the label only.
 */

export interface ProgramFieldOption {
    value: number;
    label: string;
    // * Name the ripening room uses for this value; shown as a hint in the program creator only.
    code?: string;
}

export type ProgramFieldKind = "number" | "toggle" | "select";

export interface ProgramFieldDefinition {
    key: string;
    label: string;
    unit?: string;
    kind: ProgramFieldKind;
    options?: ProgramFieldOption[];
    step?: number | "any";
    min?: number;
    max?: number;
}

export const ON_OFF_OPTIONS: ProgramFieldOption[] = [
    { value: 0, label: "Off" },
    { value: 1, label: "On" },
];

export const VENTILATION_OPTIONS: ProgramFieldOption[] = [
    { value: 0, label: "Off", code: "Off" },
    { value: 1, label: "Timed", code: "Time" },
    { value: 2, label: "Continuous", code: "Continue" },
    { value: 3, label: "CO2 sensor", code: "SNS_CO2" },
    { value: 4, label: "O2 sensor", code: "SNS_O2" },
    { value: 5, label: "C2H4 sensor", code: "SNS_C2H4" },
];

const field = (definition: ProgramFieldDefinition) => definition;

export const PROGRAM_FIELDS = {
    Enable: field({ key: "Enable", label: "Enabled", kind: "toggle", options: ON_OFF_OPTIONS }),
    TimeSpan: field({ key: "TimeSpan", label: "Duration (hours)", unit: "h", kind: "number", step: "any", min: 0 }),
    T_Start: field({ key: "T_Start", label: "Start temperature (°C)", unit: "°C", kind: "number", step: "any" }),
    T_End: field({ key: "T_End", label: "End temperature (°C)", unit: "°C", kind: "number", step: "any" }),
    Spv_O2: field({ key: "Spv_O2", label: "O2 setpoint (%)", unit: "%", kind: "number", step: "any", min: 0 }),
    Spv_CO2: field({ key: "Spv_CO2", label: "CO2 setpoint (%)", unit: "%", kind: "number", step: "any", min: 0 }),
    Spv_C2H4: field({ key: "Spv_C2H4", label: "C2H4 dosage (ppm)", unit: "ppm", kind: "number", step: "any", min: 0 }),
    Modus_Aereation: field({ key: "Modus_Aereation", label: "Ventilation", kind: "select", options: VENTILATION_OPTIONS }),
    Modus_C2H4: field({ key: "Modus_C2H4", label: "Ethylene dosing", kind: "select", options: ON_OFF_OPTIONS }),
    Modus_N2: field({ key: "Modus_N2", label: "N2 injection", kind: "select", options: ON_OFF_OPTIONS }),
    Modus_Scrubber: field({ key: "Modus_Scrubber", label: "CO2 scrubber", kind: "select", options: ON_OFF_OPTIONS }),

    FanSpeed: field({ key: "FanSpeed", label: "Fan speed (%)", unit: "%", kind: "number", step: "any", min: 0, max: 100 }),
    Aereation_Period: field({ key: "Aereation_Period", label: "Ventilation period (min)", unit: "min", kind: "number", step: "any", min: 0 }),
    Aereation_Interval: field({ key: "Aereation_Interval", label: "Ventilation interval (min)", unit: "min", kind: "number", step: "any", min: 0 }),
    T_Air_Min: field({ key: "T_Air_Min", label: "Air temperature min (°C)", unit: "°C", kind: "number", step: "any" }),
    T_Air_Max: field({ key: "T_Air_Max", label: "Air temperature max (°C)", unit: "°C", kind: "number", step: "any" }),
    T_Prod_Min: field({ key: "T_Prod_Min", label: "Product temperature min (°C)", unit: "°C", kind: "number", step: "any" }),
    T_Prod_Max: field({ key: "T_Prod_Max", label: "Product temperature max (°C)", unit: "°C", kind: "number", step: "any" }),

    Store_Setpoint: field({ key: "Store_Setpoint", label: "Storage temperature (°C)", unit: "°C", kind: "number", step: "any", min: -10, max: 40 }),
    Store_AirOffset: field({ key: "Store_AirOffset", label: "Air offset (°C)", unit: "°C", kind: "number", step: "any", min: -5, max: 5 }),
    Store_Spv_O2: field({ key: "Store_Spv_O2", label: "Storage O2 setpoint (%)", unit: "%", kind: "number", step: "any", min: 0 }),
    Store_Spv_Co2: field({ key: "Store_Spv_Co2", label: "Storage CO2 setpoint (%)", unit: "%", kind: "number", step: "any", min: 0 }),
    Store_Spv_C2H4: field({ key: "Store_Spv_C2H4", label: "Storage C2H4 dosage (ppm)", unit: "ppm", kind: "number", step: "any", min: 0 }),
    Store_Mode_Aereation: field({ key: "Store_Mode_Aereation", label: "Storage ventilation", kind: "select", options: VENTILATION_OPTIONS }),
    Store_Mode_C2H4: field({ key: "Store_Mode_C2H4", label: "Storage ethylene dosing", kind: "select", options: ON_OFF_OPTIONS }),
    Store_Mode_N2: field({ key: "Store_Mode_N2", label: "Storage N2 injection", kind: "select", options: ON_OFF_OPTIONS }),
    Store_Mode_Scrubber: field({ key: "Store_Mode_Scrubber", label: "Storage CO2 scrubber", kind: "select", options: ON_OFF_OPTIONS }),
} as const satisfies Record<string, ProgramFieldDefinition>;

export type ProgramFieldKey = keyof typeof PROGRAM_FIELDS;

export const PHASE_FIELD_KEYS = [
    "Enable", "TimeSpan", "T_Start", "T_End", "Spv_O2", "Spv_CO2", "Spv_C2H4",
    "Modus_Aereation", "Modus_C2H4", "Modus_N2", "Modus_Scrubber",
] as const satisfies readonly ProgramFieldKey[];

export const GENERAL_FIELD_KEYS = [
    "FanSpeed", "Aereation_Period", "Aereation_Interval",
    "T_Air_Min", "T_Air_Max", "T_Prod_Min", "T_Prod_Max",
] as const satisfies readonly ProgramFieldKey[];

export const STORE_FIELD_KEYS = [
    "Store_Setpoint", "Store_AirOffset", "Store_Spv_O2", "Store_Spv_Co2", "Store_Spv_C2H4",
    "Store_Mode_Aereation", "Store_Mode_C2H4", "Store_Mode_N2", "Store_Mode_Scrubber",
] as const satisfies readonly ProgramFieldKey[];

export const programField = (key: string): ProgramFieldDefinition | undefined => (
    (PROGRAM_FIELDS as Record<string, ProgramFieldDefinition>)[key]
);

export const programFieldLabel = (key: string): string => programField(key)?.label ?? key.replace(/_/g, " ");

/** Ethylene dosing is not set by hand: it is on exactly when the phase has a C2H4 setpoint above 0. */
export const ethyleneDosingFor = (setpoint: unknown): 0 | 1 => (typeof setpoint === "number" && setpoint > 0 ? 1 : 0);

/** A phase's values with the ethylene dosing mode following its C2H4 setpoint. */
export function withDerivedEthyleneDosing<T extends Record<string, number>>(values: T): T {
    return { ...values, Modus_C2H4: ethyleneDosingFor(values.Spv_C2H4) };
}

/** N2 injection pushes the oxygen out of the room: nobody may enter while it runs. */
export const injectsN2 = (values: Record<string, unknown> | undefined): boolean => Number(values?.Modus_N2) === 1;

/** A stored value as the user reads it: the option label for modes, the number otherwise. */
export function formatProgramFieldValue(key: string, value: unknown, empty = "-"): string {
    if (value === null || value === undefined || value === "") return empty;
    const definition = programField(key);
    if (definition?.options) {
        const option = definition.options.find((item) => item.value === Number(value));
        if (option) return option.label;
    }
    return String(value);
}
